import React from 'react';
import Avatar, { AvatarItem } from '@atlaskit/avatar';
import Button from '@atlaskit/button';
import DynamicTable from '@atlaskit/dynamic-table';
import ShortcutIcon from '@atlaskit/icon/core/migration/link-external--shortcut';
import Lozenge from '@atlaskit/lozenge';
import { Box, Inline, Stack, Text, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { fg } from '@atlassian/jira-feature-gating';
import { FormattedMessage } from '@atlassian/jira-intl';
import { getNavigateToTeamsApp } from '@atlassian/jira-teams-app-config/src/index.tsx';
import type { UsersAndRolesDifferenceResponse } from '../../../../types/index.tsx';
import { messages } from './messages.tsx';

export const UsersAndRolesStepTable = ({
	description,
	data,
}: {
	description?: string | React.ReactNode;
	data: UsersAndRolesDifferenceResponse;
}) => {
	const getProfileUrl = (userId: string) =>
		fg('jira-adopt-teams-nav-config')
			? getNavigateToTeamsApp({ type: 'USER', payload: { userId } }).href
			: `/jira/people/${userId}`;

	const rows = data.values.map((item, index) => ({
		key: `row-${index}`,
		cells: [
			{
				key: `cell-${index}-user`,
				content: (
					<Box paddingInlineStart="space.100">
						<Inline grow="fill" spread="space-between" alignBlock="center">
							<AvatarItem
								avatar={
									<Avatar target="_blank" href={getProfileUrl(item.id)} src={item.avatarUrl} />
								}
								primaryText={item.name}
								secondaryText={item.role}
							/>
							<Box xcss={viewProfileButtonBoxStyles}>
								{item.role === 'Admin' && fg('ops_admin_role_release') ? (
									<Lozenge>
										<FormattedMessage {...messages.tableUserCellGlobalOperationsAdminNonFinal} />
									</Lozenge>
								) : (
									<Button
										href={getProfileUrl(item.id)}
										target="_blank"
										appearance="subtle-link"
										iconAfter={<ShortcutIcon label="" color={token('color.icon.subtle')} />}
									>
										<FormattedMessage {...messages.tableUserCellActionNonFinal} />
									</Button>
								)}
							</Box>
						</Inline>
					</Box>
				),
			},
		],
	}));

	const head = {
		cells: [
			{
				key: 'user',
				content: (
					<Box paddingInlineStart="space.100">
						<FormattedMessage {...messages.tableUserCellNonFinal} />
					</Box>
				),
				isSortable: true,
			},
		],
	};

	return (
		<Stack space="space.100">
			{description && (
				<Box>
					<Text as="p" color="color.text.subtlest">
						{description}
					</Text>
				</Box>
			)}
			<DynamicTable head={head} rows={rows} rowsPerPage={5} defaultPage={1} />
		</Stack>
	);
};

const viewProfileButtonBoxStyles = xcss({
	flexShrink: 0,
});
